import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Auth, signInWithEmailAndPassword, signOut, UserCredential, user } from '@angular/fire/auth';
import { Observable, from, Subject, timer, merge, fromEvent } from 'rxjs';
import { switchMap, tap, takeUntil } from 'rxjs/operators';
import { UsuarioService } from './usuario.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  
  /** Observable of the current user (or null) */
  user$: Observable<import('@angular/fire/auth').User | null>;

  private stopIdleTimer$ = new Subject<void>();
  private readonly IDLE_TIMEOUT_MS = 3600 * 1000; // 1 hour

  constructor(
    private auth: Auth,
    private router: Router,
    private usuarioService: UsuarioService
  ) {
    this.user$ = user(this.auth);
  }

  /**
   * Attempt to log in with email & password.
   * Returns an Observable that emits the UserCredential on success
   * or errors with a FirebaseAuthError.
   */
  login(email: string, password: string): Observable<UserCredential> {
    return from(signInWithEmailAndPassword(this.auth, email, password)).pipe(
      switchMap(() => this.usuarioService.getCurrentUsuario()),
      tap(() => this.initIdleLogout())
    );
  }

  /** Log out the current user and navigate to `/login` */
  logout(): Observable<void> {
    this.stopIdleLogout();
    return from(signOut(this.auth).then(() => {
      sessionStorage.removeItem('currentUser');
      this.router.navigate(['/auth/login']);
    }));
  }

  private initIdleLogout() {
    const activityEvents$ = merge(
      fromEvent(window, 'mousemove'),
      fromEvent(window, 'click'),
      fromEvent(window, 'keydown')
    );

    activityEvents$.pipe(
      switchMap(() => timer(this.IDLE_TIMEOUT_MS)),
      takeUntil(this.stopIdleTimer$)
    ).subscribe(() => {
      console.log('User idle for 1 hour, logging out.');
      this.logout();
    });
  }

  private stopIdleLogout() {
    this.stopIdleTimer$.next();
    this.stopIdleTimer$.complete();
    this.stopIdleTimer$ = new Subject<void>(); // Recreate for next login
  }
}
